import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  createAccountingReviewDocumentRequestsBulk,
  createAccountingReviewDocumentRequest,
  getAccountingReviewDocumentDownloadUrl,
  getAccountingReviewRequestTemplates,
  getAccountingReviewSummary,
  getAccountingStripePriceAudit,
  listAccountingAccountants,
  listAccountingReviewDocumentRequests,
  listAccountingReviewDocuments,
  sendAccountingReviewDocumentRequestReminder,
  updateAccountingReviewDocumentRequestFollowup,
  updateAccountingReviewDocumentRequestAssignment,
  updateAccountingReviewDocument,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import { isSalonBusinessCpaAccountant } from "../../utils/accountingRoles";
import { accountingContextFromSearchParams } from "./accountingRouteContext";
import { currentMonthValue, monthRange } from "./accountingDate";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const statusClasses = {
  upload_requested: "border-amber-200 bg-amber-50 text-amber-700",
  received: "border-emerald-200 bg-emerald-50 text-emerald-700",
  being_reviewed: "border-sky-200 bg-sky-50 text-sky-700",
  needs_new_copy: "border-rose-200 bg-rose-50 text-rose-700",
  done: "border-slate-200 bg-slate-100 text-slate-700",
};

const followupClasses = {
  waiting_for_client: "border-slate-200 bg-slate-50 text-slate-700",
  reminder_sent: "border-sky-200 bg-sky-50 text-sky-700",
  follow_up_needed: "border-amber-200 bg-amber-50 text-amber-700",
  ready_for_review: "border-emerald-200 bg-emerald-50 text-emerald-700",
  completed: "border-slate-200 bg-white text-slate-600",
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatSize(value) {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return "Size pending";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMoney(cents) {
  const value = Number(cents || 0);
  if (!value) return "$0/month";
  return `$${Math.round(value / 100)}/month`;
}

export default function SalonBusinessCpaReviewQueue() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const accountingContext = useMemo(
    () => accountingContextFromSearchParams(searchParams),
    [searchParams]
  );
  const accountantView = isSalonBusinessCpaAccountant(user);
  const [documents, setDocuments] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [followupStatusOptions, setFollowupStatusOptions] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [accountants, setAccountants] = useState([]);
  const [stripeAudit, setStripeAudit] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [requestTemplates, setRequestTemplates] = useState([]);
  const [manualRequestTemplate, setManualRequestTemplate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [requestTarget, setRequestTarget] = useState("");
  const [manualRequestType, setManualRequestType] = useState("bank_statement");
  const [manualRequestTitle, setManualRequestTitle] = useState("");
  const [manualRequestClientNote, setManualRequestClientNote] = useState(
    "Please upload this record so we can continue your accounting review."
  );
  const [manualRequestDueDate, setManualRequestDueDate] = useState("");
  const [manualRequestPeriodLabel, setManualRequestPeriodLabel] = useState("");
  const [bulkMonth, setBulkMonth] = useState(currentMonthValue);
  const [bulkDueDate, setBulkDueDate] = useState("");
  const [bulkTemplateCodes, setBulkTemplateCodes] = useState([]);
  const [reviewNote, setReviewNote] = useState("");
  const [requestClientNote, setRequestClientNote] = useState(
    "Please upload a clearer copy so we can continue the review."
  );
  const [requestDueDate, setRequestDueDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const counts = useMemo(() => {
    return documents.reduce(
      (next, document) => {
        next.total += 1;
        if (document.status === "needs_new_copy") next.needsNewCopy += 1;
        if (document.status === "received") next.received += 1;
        if (document.status === "being_reviewed") next.reviewing += 1;
        if (document.status === "done") next.done += 1;
        return next;
      },
      { total: 0, received: 0, reviewing: 0, needsNewCopy: 0, done: 0 }
    );
  }, [documents]);

  const summaryCounts = useMemo(() => {
    return summaryRows.reduce(
      (next, row) => {
        next.total += 1;
        if (row.package_mismatch) next.packageMismatch += 1;
        if (!row.onboarding_completed) next.needsSetup += 1;
        if (row.open_request_count > 0) next.openRequests += row.open_request_count;
        if (row.overdue_request_count > 0) next.overdueRequests += row.overdue_request_count;
        return next;
      },
      { total: 0, needsSetup: 0, packageMismatch: 0, openRequests: 0, overdueRequests: 0 }
    );
  }, [summaryRows]);

  const targetOptions = useMemo(
    () =>
      summaryRows.map((row) => ({
        value: `${row.business_id}:${row.salon_id}`,
        label: `${row.business_name || "Business"} / ${row.salon_name || "Salon"}`,
        row,
      })),
    [summaryRows]
  );

  const accountantOptions = useMemo(
    () =>
      (accountants || [])
        .filter((accountant) => accountant.status === "active" || accountant.status === "invited")
        .map((accountant) => ({
          value: String(accountant.id),
          label: accountant.full_name || accountant.email,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accountants]
  );

  const loadDocuments = async () => {
    setLoading(true);
    setError("");
    try {
      const [
        data,
        requestData,
        summaryData,
        stripeAuditData,
        templateData,
        accountantData,
      ] = await Promise.all([
        listAccountingReviewDocuments(token, {
          ...accountingContext,
          status: statusFilter || undefined,
          document_type: typeFilter || undefined,
          limit: 150,
        }),
        listAccountingReviewDocumentRequests(token, {
          ...accountingContext,
          status: "needed",
          limit: 80,
        }).catch(() => null),
        getAccountingReviewSummary(token).catch(() => null),
        accountantView ? Promise.resolve(null) : getAccountingStripePriceAudit(token).catch(() => null),
        getAccountingReviewRequestTemplates(token).catch(() => null),
        accountantView ? Promise.resolve(null) : listAccountingAccountants(token).catch(() => null),
      ]);
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      setReviewRequests(Array.isArray(requestData?.requests) ? requestData.requests : []);
      setFollowupStatusOptions(
        Array.isArray(requestData?.followup_status_options)
          ? requestData.followup_status_options
          : []
      );
      setStatusOptions(Array.isArray(data?.status_options) ? data.status_options : []);
      setTypeOptions(Array.isArray(data?.document_type_options) ? data.document_type_options : []);
      const nextSummaryRows = Array.isArray(summaryData?.salons) ? summaryData.salons : [];
      setSummaryRows(nextSummaryRows);
      setStripeAudit(stripeAuditData);
      const nextTemplates = Array.isArray(templateData?.templates) ? templateData.templates : [];
      setRequestTemplates(nextTemplates);
      setAccountants(Array.isArray(accountantData?.accountants) ? accountantData.accountants : []);
      if (!bulkTemplateCodes.length && nextTemplates.length) {
        setBulkTemplateCodes(nextTemplates.slice(0, 4).map((template) => template.code));
      }
      if (!requestTarget && nextSummaryRows.length) {
        setRequestTarget(`${nextSummaryRows[0].business_id}:${nextSummaryRows[0].salon_id}`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load the review queue.");
    } finally {
      setLoading(false);
    }
  };

  const applyManualTemplate = (templateCode) => {
    setManualRequestTemplate(templateCode);
    const template = requestTemplates.find((item) => item.code === templateCode);
    if (!template) return;
    setManualRequestType(template.document_type || "other");
    setManualRequestTitle(template.title || "");
    setManualRequestClientNote(template.client_note || "");
    if (template.default_due_days) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + Number(template.default_due_days || 14));
      setManualRequestDueDate(dueDate.toISOString().slice(0, 10));
    }
  };

  const toggleBulkTemplate = (templateCode) => {
    setBulkTemplateCodes((current) =>
      current.includes(templateCode)
        ? current.filter((code) => code !== templateCode)
        : [...current, templateCode]
    );
  };

  useEffect(() => {
    if (token) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingContext, token, statusFilter, typeFilter]);

  const handleStatusChange = async (documentId, nextStatus) => {
    setBusyId(documentId);
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingReviewDocument(token, documentId, {
        status: nextStatus,
        review_note: reviewNote,
      });
      setDocuments((current) =>
        current.map((document) => (document.id === documentId ? updated : document))
      );
      setReviewNote("");
      setNotice("Document status updated.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update document status.");
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenDocument = async (documentId) => {
    setBusyId(documentId);
    setError("");
    setNotice("");
    try {
      const data = await getAccountingReviewDocumentDownloadUrl(token, documentId);
      if (data?.download_url) {
        window.open(data.download_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to open this document.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRequestNewCopy = async (document) => {
    setBusyId(document.id);
    setError("");
    setNotice("");
    try {
      await createAccountingReviewDocumentRequest(token, {
        source_document_id: document.id,
        title: `New copy needed: ${document.file_name}`,
        client_note: requestClientNote,
        internal_note: reviewNote,
        due_date: requestDueDate || null,
      });
      setDocuments((current) =>
        current.map((item) =>
          item.id === document.id
            ? { ...item, status: "needs_new_copy", status_label: "Need a new copy" }
            : item
        )
      );
      setNotice("New copy request created.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to create the document request.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateManualRequest = async (event) => {
    event.preventDefault();
    const selectedTarget = targetOptions.find((option) => option.value === requestTarget);
    if (!selectedTarget) {
      setError("Choose a salon before creating a request.");
      return;
    }
    setBusyId("manual-request");
    setError("");
    setNotice("");
    try {
      const requestTitle =
        manualRequestTitle.trim() ||
        `${typeOptions.find((option) => option.code === manualRequestType)?.label || "Document"} needed`;
      await createAccountingReviewDocumentRequest(token, {
        business_id: selectedTarget.row.business_id,
        salon_id: selectedTarget.row.salon_id,
        document_type: manualRequestType,
        title: requestTitle,
        client_note: manualRequestClientNote,
        internal_note: reviewNote,
        period_label: manualRequestPeriodLabel || null,
        due_date: manualRequestDueDate || null,
      });
      setManualRequestTitle("");
      setManualRequestDueDate("");
      setManualRequestPeriodLabel("");
      setNotice("Document request created.");
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to create the document request.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateBulkRequests = async (event) => {
    event.preventDefault();
    const selectedTarget = targetOptions.find((option) => option.value === requestTarget);
    if (!selectedTarget) {
      setError("Choose a salon before creating requests.");
      return;
    }
    if (!bulkTemplateCodes.length) {
      setError("Choose at least one request template.");
      return;
    }
    const range = monthRange(bulkMonth);
    setBusyId("bulk-request");
    setError("");
    setNotice("");
    try {
      const result = await createAccountingReviewDocumentRequestsBulk(token, {
        business_id: selectedTarget.row.business_id,
        salon_id: selectedTarget.row.salon_id,
        template_codes: bulkTemplateCodes,
        period_label: range.label || null,
        period_start: range.start_date || null,
        period_end: range.end_date || null,
        due_date: bulkDueDate || null,
        internal_note: reviewNote,
      });
      const created = Number(result?.created_count || 0);
      const skipped = Number(result?.skipped_count || 0);
      setNotice(
        skipped
          ? `${created} request${created === 1 ? "" : "s"} created. ${skipped} already existed.`
          : `${created} request${created === 1 ? "" : "s"} created.`
      );
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to create these document requests.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSendReminder = async (request) => {
    setBusyId(`reminder-${request.id}`);
    setError("");
    setNotice("");
    try {
      await sendAccountingReviewDocumentRequestReminder(token, request.id);
      setNotice("Reminder added to the owner activity history.");
      await loadDocuments();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to send this reminder.");
    } finally {
      setBusyId(null);
    }
  };

  const handleFollowupStatus = async (request, followupStatus) => {
    setBusyId(`followup-${request.id}`);
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingReviewDocumentRequestFollowup(token, request.id, {
        followup_status: followupStatus,
        internal_note: reviewNote,
      });
      setReviewRequests((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setNotice("Follow-up status updated.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update follow-up status.");
    } finally {
      setBusyId(null);
    }
  };

  const handleRequestAssignment = async (request, assignedAccountantUserId) => {
    setBusyId(`assign-${request.id}`);
    setError("");
    setNotice("");
    try {
      const updated = await updateAccountingReviewDocumentRequestAssignment(token, request.id, {
        assigned_accountant_user_id: assignedAccountantUserId
          ? Number(assignedAccountantUserId)
          : null,
      });
      setReviewRequests((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setNotice(assignedAccountantUserId ? "Request assigned." : "Request assignment cleared.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to update this assignment.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <SalonBusinessCpaShell
      title="Review queue"
      description="Review uploaded accounting, tax, payroll, GST/HST, bookkeeping, and year-end records."
      actions={
        <button
          type="button"
          onClick={loadDocuments}
          className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-5">
        {[
          ["Accounts", summaryCounts.total],
          ["Need setup", summaryCounts.needsSetup],
          ["Package review", summaryCounts.packageMismatch],
          ["Open requests", summaryCounts.openRequests],
          ["Overdue", summaryCounts.overdueRequests],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Ellyanna Accounting accounts
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Review opted-in salons, setup progress, package fit, open requests,
              and records waiting for review.
            </p>
          </div>
        </div>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="py-3 pr-4">Salon</th>
                <th className="py-3 pr-4">Setup</th>
                <th className="py-3 pr-4">Package</th>
                <th className="py-3 pr-4">Requests</th>
                <th className="py-3 pr-4">Records</th>
                <th className="py-3 pr-4">Next step</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summaryRows.length ? (
                summaryRows.slice(0, 12).map((row) => (
                  <tr key={`${row.business_id}:${row.salon_id}`} className="align-top">
                    <td className="py-3 pr-4">
                      <div className="font-semibold text-slate-900">
                        {row.salon_name || "Salon"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {row.business_name || "Business"} / Salon ID {row.salon_id}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-800">
                        {row.profile_label || "No setup profile"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {row.payroll_staff_count != null
                          ? `${row.payroll_staff_count} payroll staff`
                          : row.staff_count_range_label || "Payroll staff not set"}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className={row.package_mismatch ? "font-semibold text-amber-700" : "font-medium text-slate-800"}>
                        {row.selected_package_label || "No package selected"}
                      </div>
                      <div className="text-xs text-slate-500">
                        Recommended: {row.recommended_package_custom_quote
                          ? "Custom review"
                          : row.recommended_package_label || "Not ready"}
                      </div>
                      {row.selected_package_price_cents ? (
                        <div className="text-xs text-slate-500">
                          {formatMoney(row.selected_package_price_cents)}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4 text-slate-700">
                      {row.open_request_count} open
                      {row.overdue_request_count ? (
                        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                          {row.overdue_request_count} overdue
                        </span>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4 text-xs leading-5 text-slate-600">
                      {row.received_document_count} received<br />
                      {row.reviewing_document_count} reviewing<br />
                      {row.needs_new_copy_count} new copy
                    </td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-800">{row.next_step}</div>
                      {Array.isArray(row.missing_setup_items) &&
                      row.missing_setup_items.length ? (
                        <ul className="mt-2 max-w-xs space-y-1 text-xs leading-5 text-slate-600">
                          {row.missing_setup_items.slice(0, 4).map((item) => (
                            <li key={item}>- {item}</li>
                          ))}
                        </ul>
                      ) : null}
                      {row.package_mismatch_message ? (
                        <div className="mt-1 max-w-xs text-xs leading-5 text-amber-700">
                          {row.package_mismatch_message}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-5 text-sm text-slate-500">
                    No Ellyanna Accounting accounts found yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {stripeAudit ? (
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">
                Stripe package audit
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Checks the live Stripe monthly prices against the Ellyanna Accounting
                package amounts in Ellyanna.
              </p>
            </div>
            <span
              className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                stripeAudit.stripe_available
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              }`}
            >
              {stripeAudit.stripe_available ? "Stripe checked" : "Needs review"}
            </span>
          </div>
          {stripeAudit.message ? (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              {stripeAudit.message}
            </div>
          ) : null}
          <div className="mt-5 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="py-3 pr-4">Package</th>
                  <th className="py-3 pr-4">Ellyanna</th>
                  <th className="py-3 pr-4">Stripe</th>
                  <th className="py-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(stripeAudit.rows || []).map((row) => (
                  <tr key={row.code} className="align-top">
                    <td className="py-3 pr-4">
                      <div className="font-semibold text-slate-900">{row.label}</div>
                      <div className="text-xs text-slate-500">{row.configured_stripe_price_id || "No Stripe price ID"}</div>
                    </td>
                    <td className="py-3 pr-4 text-slate-700">
                      {formatMoney(row.expected_price_cents)}
                    </td>
                    <td className="py-3 pr-4 text-slate-700">
                      {row.live_price_cents != null ? formatMoney(row.live_price_cents) : "-"}
                      {row.live_recurring_interval ? (
                        <div className="text-xs text-slate-500">
                          {row.live_currency?.toUpperCase() || ""} / {row.live_recurring_interval}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                          row.status === "ok"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                      >
                        {row.status === "ok" ? "OK" : "Review"}
                      </span>
                      <div className="mt-1 max-w-xs text-xs leading-5 text-slate-600">
                        {row.message}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-950">
          Create monthly requests
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Choose a salon, month, and records needed. The salon owner will see each
          item as a simple upload request.
        </p>
        <form onSubmit={handleCreateBulkRequests} className="mt-5 space-y-5">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px_170px_auto] lg:items-end">
            <label className="text-sm font-semibold text-slate-700">
              Salon
              <select
                value={requestTarget}
                onChange={(event) => setRequestTarget(event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
              >
                {targetOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-semibold text-slate-700">
              Month
              <input
                type="month"
                value={bulkMonth}
                onChange={(event) => setBulkMonth(event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              />
            </label>
            <label className="text-sm font-semibold text-slate-700">
              Due date
              <input
                type="date"
                value={bulkDueDate}
                onChange={(event) => setBulkDueDate(event.target.value)}
                className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              />
            </label>
            <button
              type="submit"
              disabled={busyId === "bulk-request" || !targetOptions.length}
              className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {busyId === "bulk-request" ? "Creating..." : "Create selected"}
            </button>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-700">Records needed</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {requestTemplates.map((template) => (
                <label
                  key={template.code}
                  className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3 text-sm ${
                    bulkTemplateCodes.includes(template.code)
                      ? "border-rose-200 bg-rose-50 text-rose-900"
                      : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={bulkTemplateCodes.includes(template.code)}
                    onChange={() => toggleBulkTemplate(template.code)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block font-semibold">{template.label}</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                      {template.document_type_label}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </form>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-950">
          Create document request
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Ask a salon owner for a specific record. It appears in their document
          request checklist with an upload button.
        </p>
        <form onSubmit={handleCreateManualRequest} className="mt-5 grid gap-4 lg:grid-cols-[1fr_190px_190px_190px_1fr_160px_auto] lg:items-end">
          <label className="text-sm font-semibold text-slate-700">
            Salon
            <select
              value={requestTarget}
              onChange={(event) => setRequestTarget(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              {targetOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Template
            <select
              value={manualRequestTemplate}
              onChange={(event) => applyManualTemplate(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">Choose template</option>
              {requestTemplates.map((template) => (
                <option key={template.code} value={template.code}>
                  {template.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Record type
            <select
              value={manualRequestType}
              onChange={(event) => setManualRequestType(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              {typeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Period
            <input
              value={manualRequestPeriodLabel}
              onChange={(event) => setManualRequestPeriodLabel(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="May 2026"
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Request title
            <input
              value={manualRequestTitle}
              onChange={(event) => setManualRequestTitle(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Example: March bank statement"
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Due date
            <input
              type="date"
              value={manualRequestDueDate}
              onChange={(event) => setManualRequestDueDate(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
            />
          </label>
          <button
            type="submit"
            disabled={busyId === "manual-request" || !targetOptions.length}
            className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
          >
            {busyId === "manual-request" ? "Creating..." : "Create request"}
          </button>
          <label className="lg:col-span-7 text-sm font-semibold text-slate-700">
            Message shown to salon
            <input
              value={manualRequestClientNote}
              onChange={(event) => setManualRequestClientNote(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Please upload all pages for this record."
            />
          </label>
        </form>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Open owner requests
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Send a simple reminder when a requested record is still missing.
              The owner sees it in their activity history.
            </p>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {reviewRequests.length ? (
            reviewRequests.slice(0, 10).map((request) => (
              <article
                key={request.id}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-950">
                        {request.title}
                      </h3>
                      {request.period_label ? (
                        <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                          {request.period_label}
                        </span>
                      ) : null}
                      {request.has_unanswered_questions ? (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                          answers needed
                        </span>
                      ) : null}
                      <span
                        className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          followupClasses[request.followup_status] ||
                          "border-slate-200 bg-white text-slate-600"
                        }`}
                      >
                        {request.followup_status_label || "Waiting for client"}
                      </span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {request.business_name || "Business"} / {request.salon_name || "Salon"}
                    </p>
                    <p className="text-sm leading-6 text-slate-600">
                      {request.document_type_label}
                      {request.due_date ? ` - Due ${formatDate(request.due_date)}` : ""}
                    </p>
                    {request.reminder_count ? (
                      <p className="text-xs leading-5 text-slate-500">
                        {request.reminder_count} reminder{request.reminder_count === 1 ? "" : "s"}
                        {request.last_reminder_at_utc
                          ? `, last ${formatDate(request.last_reminder_at_utc)}`
                          : ""}
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      Assigned to{" "}
                      <span className="font-semibold text-slate-700">
                        {request.assigned_accountant_name ||
                          request.assigned_accountant_email ||
                          "Not assigned"}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 md:justify-end">
                    {accountantOptions.length ? (
                      <label className="min-w-[220px] text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                        Assign
                        <select
                          value={
                            request.assigned_accountant_user_id
                              ? String(request.assigned_accountant_user_id)
                              : ""
                          }
                          onChange={(event) =>
                            handleRequestAssignment(request, event.target.value)
                          }
                          disabled={busyId === `assign-${request.id}`}
                          className="mt-1 h-10 w-full rounded-full border border-slate-300 bg-white px-3 text-sm font-semibold normal-case tracking-normal text-slate-700 disabled:opacity-60"
                        >
                          <option value="">Not assigned</option>
                          {accountantOptions.map((accountant) => (
                            <option key={accountant.value} value={accountant.value}>
                              {accountant.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleSendReminder(request)}
                      disabled={busyId === `reminder-${request.id}`}
                      className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                    >
                      {busyId === `reminder-${request.id}` ? "Sending..." : "Send reminder"}
                    </button>
                    {followupStatusOptions
                      .filter((option) => option.code !== request.followup_status)
                      .slice(0, 3)
                      .map((option) => (
                        <button
                          key={option.code}
                          type="button"
                          onClick={() => handleFollowupStatus(request, option.code)}
                          disabled={busyId === `followup-${request.id}`}
                          className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                        >
                          {option.label}
                        </button>
                      ))}
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
              No open owner requests need reminders right now.
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-5">
        {[
          ["Total", counts.total],
          ["Received", counts.received],
          ["Reviewing", counts.reviewing],
          ["Need new copy", counts.needsNewCopy],
          ["Done", counts.done],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{value}</p>
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[180px_220px_1fr]">
          <label className="text-sm font-semibold text-slate-700">
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">All statuses</option>
              <option value="upload_requested">Upload started</option>
              {statusOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Document type
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">All types</option>
              {typeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Review note, optional
            <input
              value={reviewNote}
              onChange={(event) => setReviewNote(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Example: requested clearer bank statement"
            />
          </label>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-[1fr_180px]">
          <label className="text-sm font-semibold text-slate-700">
            Client note for new copy requests
            <input
              value={requestClientNote}
              onChange={(event) => setRequestClientNote(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
              placeholder="Please upload all pages of the statement."
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Due date
            <input
              type="date"
              value={requestDueDate}
              onChange={(event) => setRequestDueDate(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
            />
          </label>
        </div>
        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}
        {notice ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {notice}
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading review queue...
          </div>
        ) : documents.length ? (
          documents.map((document) => (
            <article
              key={document.id}
              className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-slate-950">
                      {document.file_name}
                    </h2>
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                        statusClasses[document.status] ||
                        "border-slate-200 bg-slate-100 text-slate-700"
                      }`}
                    >
                      {document.status_label}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {document.business_name || "Business"} / {document.salon_name || "Salon"}
                  </p>
                  <p className="text-sm leading-6 text-slate-600">
                    {document.document_type_label} - {formatSize(document.file_size)} - Uploaded {formatDate(document.created_at_utc)}
                  </p>
                  <p className="text-sm leading-6 text-slate-600">
                    Uploaded by {document.uploaded_by_name || document.uploaded_by_email || "Unknown user"}
                  </p>
                  {document.request_id ? (
                    <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      <p className="font-semibold">
                        Uploaded for request: {document.request_title || `Request #${document.request_id}`}
                      </p>
                      <p className="mt-1">
                        Request status: {document.request_status_label || "Open"}
                        {document.request_due_date
                          ? ` - Due ${formatDate(document.request_due_date)}`
                          : ""}
                        {document.request_period_label
                          ? ` - ${document.request_period_label}`
                          : ""}
                      </p>
                    </div>
                  ) : null}
                  {document.note ? (
                    <p className="mt-2 rounded-2xl bg-slate-50 p-3 text-sm text-slate-600">
                      {document.note}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => handleOpenDocument(document.id)}
                    disabled={busyId === document.id}
                    className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    Open document
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRequestNewCopy(document)}
                    disabled={busyId === document.id}
                    className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                  >
                    Request new copy
                  </button>
                  {statusOptions.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      onClick={() => handleStatusChange(document.id, option.code)}
                      disabled={busyId === document.id || document.status === option.code}
                      className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
            No documents match these filters.
          </div>
        )}
      </section>
    </SalonBusinessCpaShell>
  );
}
